import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { HiOutlineTrash, HiPlus } from 'react-icons/hi';
import { getStoreInfo, getStoreSettings, createStoreSettings, updateStoreSettings } from '../../api/stores';

const DAYS = ['월', '화', '수', '목', '금', '토', '일'];
const WEEKDAYS = ['월', '화', '수', '목', '금'];
const WEEKEND = ['토', '일'];

const makeDefaultHours = () =>
    Object.fromEntries(DAYS.map((d) => [d, { enabled: false, slots: [] }]));

const timeToMins = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
};

const minsToTime = (mins) => {
    const h = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const isSlotInvalid = (slots, idx) => {
    const s = slots[idx];
    const start = timeToMins(s.start);
    const end = timeToMins(s.end);
    if (start >= end) return true;
    return slots.some((other, i) => {
        if (i === idx) return false;
        return start < timeToMins(other.end) && end > timeToMins(other.start);
    });
};

const DEFAULT_SLOT = { start: '09:00', end: '18:00' };

const DAY_TO_EN = { '월': 'mon', '화': 'tue', '수': 'wed', '목': 'thu', '금': 'fri', '토': 'sat', '일': 'sun' };
const EN_TO_DAY = { mon: '월', tue: '화', wed: '수', thu: '목', fri: '금', sat: '토', sun: '일' };

const apiHoursToState = (apiHours) => {
    const state = makeDefaultHours();
    if (!apiHours) return state;
    Object.entries(apiHours).forEach(([enKey, slots]) => {
        const day = EN_TO_DAY[enKey];
        if (day && slots && slots.length > 0) {
            state[day] = { enabled: true, slots: slots.map((s) => ({ start: s.start, end: s.end })) };
        }
    });
    return state;
};

const stateToApiHours = (운영시간) => {
    const result = {};
    DAYS.forEach((day) => {
        const { enabled, slots } = 운영시간[day];
        if (enabled && slots.length > 0) {
            result[DAY_TO_EN[day]] = slots.map((s) => ({ start: s.start, end: s.end }));
        }
    });
    return result;
};

const StoreInfoPage = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const [스토어, set스토어] = useState(null);
    const [로딩중, set로딩중] = useState(true);
    const [에러, set에러] = useState(null);
    const [운영시간, set운영시간] = useState(makeDefaultHours());
    const [평일슬롯, set평일슬롯] = useState([{ ...DEFAULT_SLOT }]);
    const [예약활성화, set예약활성화] = useState(false);
    const [모달열림, set모달열림] = useState(false);
    const [저장중, set저장중] = useState(false);
    const [저장완료, set저장완료] = useState(false);
    const [설정존재, set설정존재] = useState(false);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const data = await getStoreSettings(uuid);
                console.log('[store settings GET]', data);
                set설정존재(true);
                return data;
            } catch (error) {
                console.log('[store settings GET error]', error?.response?.status, error?.response?.data);
                if (error?.response?.status === 404) {
                    return null;
                }
                return null;
            }
        };

        Promise.all([getStoreInfo(uuid), loadSettings()])
            .then(([storeData, settingsData]) => {
                set스토어(storeData?.store ?? storeData);
                if (settingsData) {
                    const s = settingsData.settings ?? settingsData;
                    set예약활성화(Boolean(s.reservation_enabled));
                    set운영시간(apiHoursToState(s.reservation_hours));
                }
            })
            .catch(() => { set에러('정보를 불러오지 못했습니다.'); })
            .finally(() => { set로딩중(false); });
    }, [uuid]);

    const save = async () => {
        set저장중(true);
        const payload = {
            reservation_enabled: 예약활성화,
            reservation_hours: stateToApiHours(운영시간),
        };
        try {
            if (설정존재) {
                await updateStoreSettings(uuid, payload);
            } else {
                await createStoreSettings(uuid, payload);
                set설정존재(true);
            }
            set저장완료(true);
            setTimeout(() => set저장완료(false), 2000);
        } finally {
            set저장중(false);
        }
    };

    const handleModalConfirm = async () => {
        await save();
        set모달열림(false);
    };

    const handleToggle = () => {
        if (!예약활성화) {
            set예약활성화(true);
            set모달열림(true);
        } else {
            set예약활성화(false);
        }
    };

    const applyTodays = (days, slots) => {
        set운영시간((prev) => {
            const next = { ...prev };
            days.forEach((d) => {
                next[d] = { enabled: slots.length > 0, slots: slots.map((s) => ({ ...s })) };
            });
            return next;
        });
    };

    const applyPreset = (days) => {
        applyTodays(days, [{ ...DEFAULT_SLOT }]);
    };

    const resetAll = () => set운영시간(makeDefaultHours());

    const toggleDay = (day) => {
        set운영시간((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                enabled: !prev[day].enabled,
                slots: prev[day].enabled ? [] : [{ ...DEFAULT_SLOT }],
            },
        }));
    };

    const addSlot = (day) => {
        set운영시간((prev) => {
            const slots = prev[day].slots;
            let newStart = '09:00';
            let newEnd = '10:00';
            if (slots.length > 0) {
                const maxEnd = Math.max(...slots.map((s) => timeToMins(s.end)));
                newStart = minsToTime(maxEnd);
                newEnd = minsToTime(Math.min(maxEnd + 60, 23 * 60 + 59));
            }
            return {
                ...prev,
                [day]: { ...prev[day], slots: [...slots, { start: newStart, end: newEnd }] },
            };
        });
    };

    const removeSlot = (day, idx) => {
        set운영시간((prev) => {
            const newSlots = prev[day].slots.filter((_, i) => i !== idx);
            return {
                ...prev,
                [day]: { enabled: newSlots.length > 0, slots: newSlots },
            };
        });
    };

    const updateSlot = (day, idx, field, value) => {
        set운영시간((prev) => {
            const newSlots = prev[day].slots.map((s, i) =>
                i === idx ? { ...s, [field]: value } : s
            );
            return { ...prev, [day]: { ...prev[day], slots: newSlots } };
        });
    };

    const addBulkSlot = () => {
        const slots = 평일슬롯;
        let newStart = '09:00';
        let newEnd = '10:00';
        if (slots.length > 0) {
            const maxEnd = Math.max(...slots.map((s) => timeToMins(s.end)));
            newStart = minsToTime(maxEnd);
            newEnd = minsToTime(Math.min(maxEnd + 60, 23 * 60 + 59));
        }
        set평일슬롯([...slots, { start: newStart, end: newEnd }]);
    };

    const removeBulkSlot = (idx) => {
        set평일슬롯((prev) => prev.filter((_, i) => i !== idx));
    };

    const updateBulkSlot = (idx, field, value) => {
        set평일슬롯((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
    };

    const isHoursInvalid = DAYS.some((d) =>
        운영시간[d].slots.some((_, idx) => isSlotInvalid(운영시간[d].slots, idx))
    );

    const renderHoursUI = () => (
        <>
            <PresetBar>
                <PresetLabel>빠른 설정</PresetLabel>
                <PresetButton onClick={() => applyPreset(WEEKDAYS)}>평일</PresetButton>
                <PresetButton onClick={() => applyPreset(WEEKEND)}>주말</PresetButton>
                <PresetButton onClick={() => applyPreset(DAYS)}>매일</PresetButton>
                <ResetButton onClick={resetAll}>초기화</ResetButton>
            </PresetBar>

            <BulkCard>
                <BulkHeader>
                    <BulkTitle>평일 (월~금) 일괄 설정</BulkTitle>
                    <BulkDesc>아래 시간을 설정하고 적용하면 월~금에 동일하게 반영돼요</BulkDesc>
                </BulkHeader>
                <BulkBody>
                    <BulkSlots>
                        {평일슬롯.map((slot, idx) => {
                            const invalid = isSlotInvalid(평일슬롯, idx);
                            return (
                                <SlotRow key={idx}>
                                    <TimeInput
                                        type="time"
                                        value={slot.start}
                                        $invalid={invalid}
                                        onChange={(e) => updateBulkSlot(idx, 'start', e.target.value)}
                                    />
                                    <SlotSep>~</SlotSep>
                                    <TimeInput
                                        type="time"
                                        value={slot.end}
                                        $invalid={invalid}
                                        onChange={(e) => updateBulkSlot(idx, 'end', e.target.value)}
                                    />
                                    {평일슬롯.length > 1 && (
                                        <DeleteSlotButton onClick={() => removeBulkSlot(idx)}>
                                            <HiOutlineTrash size={16} />
                                        </DeleteSlotButton>
                                    )}
                                </SlotRow>
                            );
                        })}
                        {평일슬롯.some((_, idx) => isSlotInvalid(평일슬롯, idx)) && (
                            <OverlapWarning>중복된 시간대가 있어요</OverlapWarning>
                        )}
                        <AddSlotButton onClick={addBulkSlot}>
                            <HiPlus size={14} />
                            시간 추가
                        </AddSlotButton>
                    </BulkSlots>
                    <ApplyButton
                        onClick={() => applyTodays(WEEKDAYS, 평일슬롯)}
                        disabled={평일슬롯.some((_, idx) => isSlotInvalid(평일슬롯, idx))}
                    >
                        월~금에 적용
                    </ApplyButton>
                </BulkBody>
            </BulkCard>

            <HoursCard>
                {DAYS.map((day) => {
                    const { enabled, slots } = 운영시간[day];
                    return (
                        <DayRow key={day}>
                            <DayBadge $active={enabled} onClick={() => toggleDay(day)}>
                                {day}
                            </DayBadge>
                            <DayContent>
                                {!enabled ? (
                                    <EmptySlotText>가능한 시간 없음</EmptySlotText>
                                ) : (
                                    <>
                                        {slots.map((slot, idx) => {
                                            const invalid = isSlotInvalid(slots, idx);
                                            return (
                                                <SlotRow key={idx}>
                                                    <TimeInput
                                                        type="time"
                                                        value={slot.start}
                                                        $invalid={invalid}
                                                        onChange={(e) => updateSlot(day, idx, 'start', e.target.value)}
                                                    />
                                                    <SlotSep>~</SlotSep>
                                                    <TimeInput
                                                        type="time"
                                                        value={slot.end}
                                                        $invalid={invalid}
                                                        onChange={(e) => updateSlot(day, idx, 'end', e.target.value)}
                                                    />
                                                    <DeleteSlotButton onClick={() => removeSlot(day, idx)}>
                                                        <HiOutlineTrash size={16} />
                                                    </DeleteSlotButton>
                                                </SlotRow>
                                            );
                                        })}
                                        {slots.some((_, idx) => isSlotInvalid(slots, idx)) && (
                                            <OverlapWarning>중복된 시간대가 있어요</OverlapWarning>
                                        )}
                                        <AddSlotButton onClick={() => addSlot(day)}>
                                            <HiPlus size={14} />
                                            시간 추가
                                        </AddSlotButton>
                                    </>
                                )}
                            </DayContent>
                        </DayRow>
                    );
                })}
            </HoursCard>
        </>
    );

    if (로딩중) return <Page><Body><Inner><StatusText>불러오는 중...</StatusText></Inner></Body></Page>;
    if (에러) return <Page><Body><Inner><StatusText $error>{에러}</StatusText></Inner></Body></Page>;

    return (
        <Page>
            <Body>
                <Inner>
                    <BackButton onClick={() => navigate('/stores')}>← 스토어 목록</BackButton>

                    <TitleArea>
                        <PageBadge>스토어 기본 / 운영시간</PageBadge>
                        <PageTitle>{스토어?.name ?? '스토어'}</PageTitle>
                        <PageDesc>
                            스토어의 기본 정보와 운영시간을 확인하고 관리합니다.
                        </PageDesc>
                    </TitleArea>

                    <Section>
                        <SectionTitle>기본 정보</SectionTitle>
                        <InfoCard>
                            <InfoRow>
                                <InfoLabel>스토어명</InfoLabel>
                                <InfoValue>{스토어?.name ?? '-'}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>전화번호</InfoLabel>
                                <InfoValue>{스토어?.phone ?? '-'}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>주소</InfoLabel>
                                <InfoValue>{스토어?.address ?? '-'}</InfoValue>
                            </InfoRow>
                        </InfoCard>
                    </Section>

                    <Section>
                        <SectionTitle>예약 기능</SectionTitle>
                        <ToggleCard>
                            <ToggleInfo>
                                <ToggleLabel>예약 기능 활성화</ToggleLabel>
                                <ToggleDesc>
                                    활성화하면 AI 에이전트가 예약을 접수받습니다.
                                </ToggleDesc>
                            </ToggleInfo>
                            <ToggleSwitch
                                $on={예약활성화}
                                onClick={handleToggle}
                                role="switch"
                                aria-checked={예약활성화}
                            >
                                <ToggleThumb $on={예약활성화} />
                            </ToggleSwitch>
                        </ToggleCard>
                    </Section>

                    {예약활성화 && (
                        <Section>
                            <SectionTitleRow>
                                <SectionTitle>운영 / 서비스 제공 가능 시간</SectionTitle>
                                <EditHoursButton onClick={() => set모달열림(true)}>
                                    시간 수정
                                </EditHoursButton>
                            </SectionTitleRow>
                            {renderHoursUI()}
                            <SaveRow>
                                {저장완료 && <SavedText>저장됐어요</SavedText>}
                                <SaveButton onClick={save} disabled={저장중 || isHoursInvalid}>
                                    {저장중 ? '저장 중...' : '저장'}
                                </SaveButton>
                            </SaveRow>
                        </Section>
                    )}
                </Inner>
            </Body>

            {모달열림 && createPortal(
                <ModalBackdrop onClick={() => set모달열림(false)}>
                    <ModalContainer onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>운영 / 서비스 제공 가능 시간 설정</ModalTitle>
                            <ModalSubtitle>예약을 받을 시간대를 설정해주세요</ModalSubtitle>
                        </ModalHeader>
                        <ModalBody>
                            {renderHoursUI()}
                        </ModalBody>
                        <ModalFooter>
                            <ModalConfirmButton
                                onClick={handleModalConfirm}
                                disabled={저장중 || isHoursInvalid}
                            >
                                {저장중 ? '저장 중...' : '완료'}
                            </ModalConfirmButton>
                        </ModalFooter>
                    </ModalContainer>
                </ModalBackdrop>,
                document.body
            )}
        </Page>
    );
};


const Page = styled.div`
    width: 100%;
    min-height: 100dvh;
    background: #f7f8fa;
    font-family: Pretendard, -apple-system, sans-serif;
`;

const Body = styled.div`
    padding-top: 80px;
    padding-bottom: 60px;
    display: flex;
    justify-content: center;
`;

const Inner = styled.div`
    width: 100%;
    max-width: 860px;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    align-self: flex-start;
    &:hover { color: #333333; }
`;

const TitleArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const PageBadge = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #487BFD;
    letter-spacing: 0.1em;
    text-transform: uppercase;
`;

const PageTitle = styled.h1`
    font-size: 24px;
    font-weight: 800;
    color: #111111;
    letter-spacing: -0.02em;
    margin: 0;
`;

const PageDesc = styled.p`
    font-size: 14px;
    color: #999999;
    line-height: 1.65;
    margin: 0;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SectionTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const SectionTitle = styled.h2`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 700;
    color: #888888;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin: 0;
`;

const EditHoursButton = styled.button`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    color: #487BFD;
    background: #eef2ff;
    border: none;
    border-radius: 8px;
    padding: 5px 12px;
    cursor: pointer;
    transition: background 0.12s;
    &:hover { background: #dde6ff; }
`;

const InfoCard = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    overflow: hidden;
`;

const InfoRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 20px;

    & + & {
        border-top: 1px solid #f5f5f5;
    }
`;

const InfoLabel = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #888888;
    min-width: 80px;
    flex-shrink: 0;
`;

const InfoValue = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #111111;
`;

const StatusText = styled.p`
    font-family: Pretendard;
    font-size: 14px;
    color: ${({ $error }) => $error ? '#ff4444' : '#888888'};
    padding: 32px 0;
`;

/* ── 예약 기능 토글 ── */
const ToggleCard = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    padding: 18px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

const ToggleInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ToggleLabel = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    color: #111111;
`;

const ToggleDesc = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    color: #aaaaaa;
    line-height: 1.5;
`;

const ToggleSwitch = styled.button`
    flex-shrink: 0;
    width: 48px;
    height: 28px;
    border-radius: 14px;
    border: none;
    background: ${({ $on }) => $on ? '#487BFD' : '#e0e0e0'};
    cursor: pointer;
    padding: 3px;
    display: flex;
    align-items: center;
    justify-content: ${({ $on }) => $on ? 'flex-end' : 'flex-start'};
    transition: background 0.2s, justify-content 0.2s;
`;

const ToggleThumb = styled.div`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.18);
    transition: transform 0.2s;
`;

/* ── 저장 버튼 ── */
const SaveRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
`;

const SavedText = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    color: #1e7a5a;
    font-weight: 500;
`;

const SaveButton = styled.button`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    background: #487BFD;
    border: none;
    border-radius: 10px;
    padding: 10px 28px;
    cursor: pointer;
    transition: background 0.12s;

    &:hover:not(:disabled) { background: #2f63e8; }
    &:disabled { background: #cccccc; cursor: default; }
`;

/* ── 운영시간 설정 모달 ── */
const ModalBackdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 24px 16px;
`;

const ModalContainer = styled.div`
    background: #ffffff;
    border-radius: 16px;
    width: 100%;
    max-width: 600px;
    max-height: 90dvh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
`;

const ModalHeader = styled.div`
    padding: 24px 24px 16px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
`;

const ModalTitle = styled.h2`
    font-family: Pretendard;
    font-size: 18px;
    font-weight: 800;
    color: #111111;
    margin: 0 0 4px;
    letter-spacing: -0.02em;
`;

const ModalSubtitle = styled.p`
    font-family: Pretendard;
    font-size: 14px;
    color: #aaaaaa;
    margin: 0;
`;

const ModalBody = styled.div`
    padding: 20px 24px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
`;

const ModalFooter = styled.div`
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    flex-shrink: 0;
`;

const ModalConfirmButton = styled.button`
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    color: #ffffff;
    background: #487BFD;
    border: none;
    border-radius: 10px;
    padding: 10px 28px;
    cursor: pointer;
    transition: background 0.12s;

    &:hover:not(:disabled) { background: #2f63e8; }
    &:disabled { background: #cccccc; cursor: default; }
`;

/* ── A: 빠른 프리셋 ── */
const PresetBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const PresetLabel = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #aaaaaa;
    margin-right: 4px;
`;

const PresetButton = styled.button`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    color: #487BFD;
    background: #eef2ff;
    border: none;
    border-radius: 8px;
    padding: 6px 14px;
    cursor: pointer;
    transition: background 0.12s;

    &:hover { background: #dde6ff; }
`;

const ResetButton = styled.button`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #aaaaaa;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    padding: 6px 14px;
    cursor: pointer;
    margin-left: auto;
    transition: background 0.12s, color 0.12s;

    &:hover { background: #eeeeee; color: #555555; }
`;

/* ── C: 평일 일괄 설정 ── */
const BulkCard = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    overflow: hidden;
`;

const BulkHeader = styled.div`
    padding: 14px 20px 12px;
    border-bottom: 1px solid #f5f5f5;
`;

const BulkTitle = styled.div`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    color: #333333;
    margin-bottom: 2px;
`;

const BulkDesc = styled.div`
    font-family: Pretendard;
    font-size: 12px;
    color: #aaaaaa;
`;

const BulkBody = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 14px 20px;
`;

const BulkSlots = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
`;

const ApplyButton = styled.button`
    flex-shrink: 0;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    background: #1e7a5a;
    border: none;
    border-radius: 8px;
    padding: 8px 16px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.12s;
    align-self: flex-start;

    &:hover:not(:disabled) { background: #185f46; }
    &:disabled { background: #cccccc; cursor: default; }
`;

/* ── 요일별 카드 ── */
const HoursCard = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    overflow: hidden;
`;

const DayRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 14px 20px;

    & + & {
        border-top: 1px solid #f5f5f5;
    }
`;

const DayBadge = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: none;
    flex-shrink: 0;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    background: ${({ $active }) => $active ? '#1e7a5a' : '#f0f0f0'};
    color: ${({ $active }) => $active ? '#ffffff' : '#999999'};

    &:hover {
        background: ${({ $active }) => $active ? '#185f46' : '#e0e0e0'};
    }
`;

const DayContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    padding-top: 6px;
`;

const EmptySlotText = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    color: #cccccc;
    line-height: 24px;
`;

const SlotRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const TimeInput = styled.input`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #111111;
    border: 1px solid ${({ $invalid }) => $invalid ? '#ff4444' : '#e0e0e0'};
    border-radius: 8px;
    padding: 6px 10px;
    background: ${({ $invalid }) => $invalid ? '#fff5f5' : '#fafafa'};
    outline: none;
    width: 140px;
    cursor: pointer;

    &:focus {
        border-color: ${({ $invalid }) => $invalid ? '#ff4444' : '#487BFD'};
        background: #ffffff;
    }
`;

const SlotSep = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    color: #aaaaaa;
`;

const DeleteSlotButton = styled.button`
    border: none;
    background: none;
    padding: 6px;
    color: #cccccc;
    cursor: pointer;
    border-radius: 6px;
    display: flex;
    align-items: center;
    transition: color 0.12s, background 0.12s;

    &:hover {
        color: #ff4444;
        background: #fff0f0;
    }
`;

const OverlapWarning = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #ff4444;
`;

const AddSlotButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    border: 1px dashed #d0d0d0;
    background: none;
    border-radius: 8px;
    padding: 5px 10px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    align-self: flex-start;
    transition: border-color 0.12s, color 0.12s;

    &:hover {
        border-color: #487BFD;
        color: #487BFD;
    }
`;


export default StoreInfoPage;
